import { type QueryKey } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  coverageFilesTableDefinition,
  type CoverageFilesSortKey,
} from '@/features/codeCoverage/pages/coverageFilesTableDefinition';
import {
  coverageRunsTableDefinition,
  type CoverageRunsSortKey,
} from '@/features/codeCoverage/pages/coverageRunsTableDefinition';
import { type CoverageTableTab } from '@/features/codeCoverage/pages/CoverageTablesPanel';
import {
  uploadsTableDefinition,
  type UploadsSortKey,
} from '@/features/executionRuns/pages/uploadsTableDefinition';
import { gridPaths, queryKeys } from '@/shared/hooks';
import type { GridColumnVisibilityModel } from '@/ui/primitives';

type CoverageTablesState = {
  activeTableTab: CoverageTableTab;
  coverageFilesColumnVisibilityModel: GridColumnVisibilityModel;
  coverageFilesSortDirection: 'asc' | 'desc';
  coverageFilesSortKey: CoverageFilesSortKey;
  coverageFilesToolbarQueryKey: QueryKey;
  coverageRunsColumnVisibilityModel: GridColumnVisibilityModel;
  coverageRunsSortDirection: 'asc' | 'desc';
  coverageRunsSortKey: CoverageRunsSortKey;
  coverageRunsTableQueryKey: QueryKey;
  onCoverageFilesSortChange: (
    nextSortKey: CoverageFilesSortKey,
    nextSortDirection: 'asc' | 'desc',
  ) => void;
  onCoverageRunsSortChange: (
    nextSortKey: CoverageRunsSortKey,
    nextSortDirection: 'asc' | 'desc',
  ) => void;
  onUploadsSortChange: (nextSortKey: UploadsSortKey, nextSortDirection: 'asc' | 'desc') => void;
  setActiveTableTab: (nextTab: CoverageTableTab) => void;
  setCoverageFilesColumnVisibilityModel: (nextModel: GridColumnVisibilityModel) => void;
  setCoverageRunsColumnVisibilityModel: (nextModel: GridColumnVisibilityModel) => void;
  setUploadsColumnVisibilityModel: (nextModel: GridColumnVisibilityModel) => void;
  uploadsColumnVisibilityModel: GridColumnVisibilityModel;
  uploadsSortDirection: 'asc' | 'desc';
  uploadsSortKey: UploadsSortKey;
  uploadsToolbarQueryKey: QueryKey;
};

export const useCoverageTablesState = (): CoverageTablesState => {
  const [activeTableTab, setActiveTableTab] = useState<CoverageTableTab>('coverage-runs');
  const [coverageRunsColumnVisibilityModel, setCoverageRunsColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(coverageRunsTableDefinition.defaultColumnVisibilityModel);
  const [coverageRunsSortKey, setCoverageRunsSortKey] = useState<CoverageRunsSortKey>(
    coverageRunsTableDefinition.defaultSortKey,
  );
  const [coverageRunsSortDirection, setCoverageRunsSortDirection] = useState<'asc' | 'desc'>(
    coverageRunsTableDefinition.defaultSortDirection,
  );
  const [coverageFilesColumnVisibilityModel, setCoverageFilesColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(coverageFilesTableDefinition.defaultColumnVisibilityModel);
  const [coverageFilesSortKey, setCoverageFilesSortKey] = useState<CoverageFilesSortKey>(
    coverageFilesTableDefinition.defaultSortKey,
  );
  const [coverageFilesSortDirection, setCoverageFilesSortDirection] = useState<'asc' | 'desc'>(
    coverageFilesTableDefinition.defaultSortDirection,
  );
  const [uploadsColumnVisibilityModel, setUploadsColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(uploadsTableDefinition.defaultColumnVisibilityModel);
  const [uploadsSortKey, setUploadsSortKey] = useState<UploadsSortKey>(
    uploadsTableDefinition.defaultSortKey,
  );
  const [uploadsSortDirection, setUploadsSortDirection] = useState<'asc' | 'desc'>(
    uploadsTableDefinition.defaultSortDirection,
  );

  const coverageRunsTableQueryKey = useMemo(
    () =>
      [
        ...queryKeys.dataGrid(gridPaths.coverageRuns),
        'toolbar',
        coverageRunsSortKey,
        coverageRunsSortDirection,
      ] as const,
    [coverageRunsSortDirection, coverageRunsSortKey],
  );
  const coverageFilesToolbarQueryKey = useMemo(
    () =>
      [
        ...queryKeys.dataGrid(gridPaths.coverageFiles),
        'toolbar',
        coverageFilesSortKey,
        coverageFilesSortDirection,
      ] as const,
    [coverageFilesSortDirection, coverageFilesSortKey],
  );
  const uploadsToolbarQueryKey = useMemo(
    () =>
      [
        ...queryKeys.dataGrid(gridPaths.uploads),
        'coverage-toolbar',
        uploadsSortKey,
        uploadsSortDirection,
      ] as const,
    [uploadsSortDirection, uploadsSortKey],
  );

  return {
    activeTableTab,
    coverageFilesColumnVisibilityModel,
    coverageFilesSortDirection,
    coverageFilesSortKey,
    coverageFilesToolbarQueryKey,
    coverageRunsColumnVisibilityModel,
    coverageRunsSortDirection,
    coverageRunsSortKey,
    coverageRunsTableQueryKey,
    onCoverageFilesSortChange: (nextSortKey, nextSortDirection) => {
      setCoverageFilesSortKey(nextSortKey);
      setCoverageFilesSortDirection(nextSortDirection);
    },
    onCoverageRunsSortChange: (nextSortKey, nextSortDirection) => {
      setCoverageRunsSortKey(nextSortKey);
      setCoverageRunsSortDirection(nextSortDirection);
    },
    onUploadsSortChange: (nextSortKey, nextSortDirection) => {
      setUploadsSortKey(nextSortKey);
      setUploadsSortDirection(nextSortDirection);
    },
    setActiveTableTab,
    setCoverageFilesColumnVisibilityModel,
    setCoverageRunsColumnVisibilityModel,
    setUploadsColumnVisibilityModel,
    uploadsColumnVisibilityModel,
    uploadsSortDirection,
    uploadsSortKey,
    uploadsToolbarQueryKey,
  };
};
